// src/utils/repImport.ts
//
// Pure, dependency-free CSV import logic for the rep roster (Story 1.2).
// The browser reads the file, this module parses + validates it into
// candidate reps and a rejection report, and repsService upserts the
// candidates under the admin's JWT. Two validation layers, one authority:
// this produces most of the report cheaply; the DB triggers are the final
// word (concurrent edits, duplicate phones vs existing rows). Both layers'
// reasons land in the same ImportReport shape.
//
// No new dependency: parseCsv is a ~50-line RFC-4180 subset, and
// normalizePhoneUs mirrors the canonical SQL yak_normalize_phone() (the SQL
// function is authoritative; this exists for friendly pre-flight rejection).

import type { YakRegion } from '../types/yak'

// A candidate ready to upsert. `line` is its 1-based position among
// non-blank CSV rows (header = line 1) so a later DB-side failure can be
// reported against the row the admin sees.
export interface CandidateRep {
  line: number
  name: string
  email: string
  phones: string[]
  region_id: string
}

export interface ImportRowRejection {
  line: number
  email: string | null
  reason: string
}

export interface ImportReport {
  accepted: number
  rejected: number
  rejections: ImportRowRejection[]
}

// Result of parsing + validating a file client-side. A file-level error
// (empty file, missing header column) blocks the whole import; row-level
// problems become rejections while accepted rows still import.
export type ParsedImport =
  | { ok: false; error: string }
  | { ok: true; candidates: CandidateRep[]; rejections: ImportRowRejection[] }

// One data row after header-column extraction; strings only, untrimmed
// values already trimmed at the cell level.
interface RawRepRow {
  line: number
  name: string
  email: string
  phones: string
  region: string
}

const REQUIRED_COLUMNS = ['name', 'phones', 'email', 'region'] as const

// Minimal RFC-4180 subset: quoted fields, "" escapes a quote, commas and
// newlines inside quotes are literal, CRLF or LF ends a record, and fully
// blank lines are skipped (so a trailing newline doesn't invent an empty
// row). An explicitly quoted empty field ("") is kept — it's meaningful.
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let field = ''
  let record: string[] = []
  let inQuotes = false
  let fieldQuoted = false
  let recordHasContent = false
  let i = 0
  const n = text.length

  const endField = () => {
    record.push(field)
    if (field.length > 0 || fieldQuoted) recordHasContent = true
    field = ''
    fieldQuoted = false
  }
  const endRecord = () => {
    endField()
    // A record that is a single empty, never-quoted field is a blank line.
    if (!(record.length === 1 && record[0] === '' && !recordHasContent)) {
      rows.push(record)
    }
    record = []
    recordHasContent = false
  }

  while (i < n) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i += 2
          continue
        }
        inQuotes = false
        i++
        continue
      }
      field += c
      i++
      continue
    }
    if (c === '"') {
      inQuotes = true
      fieldQuoted = true
      i++
      continue
    }
    if (c === ',') {
      endField()
      i++
      continue
    }
    if (c === '\r') {
      if (text[i + 1] === '\n') i++
      endRecord()
      i++
      continue
    }
    if (c === '\n') {
      endRecord()
      i++
      continue
    }
    field += c
    i++
  }
  // Flush a final record with no trailing newline.
  if (field.length > 0 || fieldQuoted || record.length > 0) {
    endRecord()
  }
  return rows
}

// US-only E.164 normalization, mirroring the canonical SQL
// yak_normalize_phone(). Strips space - ( ) . separators; accepts
// +1XXXXXXXXXX / 1XXXXXXXXXX / bare 10 digits; returns +1XXXXXXXXXX or null.
export function normalizePhoneUs(raw: string): string | null {
  if (raw == null) return null
  const digits = raw.replace(/[\s\-().]/g, '')
  if (/^\+1\d{10}$/.test(digits)) return digits
  if (/^1\d{10}$/.test(digits)) return '+' + digits
  if (/^\d{10}$/.test(digits)) return '+1' + digits
  return null
}

// Format a canonical +1XXXXXXXXXX as (XXX) XXX-XXXX for display/report copy
// (mirrors the SQL yak_format_phone). Non-canonical input is returned as-is.
export function formatPhoneUs(e164: string): string {
  return /^\+1\d{10}$/.test(e164)
    ? `(${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}`
    : e164
}

// Basic email shape for friendly pre-flight; the DB CHECK is authoritative.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Shared so the per-rep edit path (repsService) rejects a malformed email
// with the same friendly copy the import uses, instead of letting the DB
// email CHECK (23514) leak raw constraint text. The DB remains the authority.
export function isPlausibleEmail(email: string): boolean {
  return EMAIL_RE.test(email)
}

// Resolve a leaf-region assignment context from the org's region list.
interface RegionResolver {
  byExact: Map<string, YakRegion>
  byLower: Map<string, YakRegion[]>
  parentIds: Set<string>
}

function buildRegionResolver(regions: YakRegion[]): RegionResolver {
  const byExact = new Map<string, YakRegion>()
  const byLower = new Map<string, YakRegion[]>()
  const parentIds = new Set<string>()
  for (const r of regions) {
    byExact.set(r.name.trim(), r)
    const key = r.name.trim().toLowerCase()
    const bucket = byLower.get(key)
    if (bucket) bucket.push(r)
    else byLower.set(key, [r])
    if (r.parent_region_id) parentIds.add(r.parent_region_id)
  }
  return { byExact, byLower, parentIds }
}

// Validate each data row, resolving regions and normalizing phones. Returns
// accepted candidates and rejections; a duplicate email inside the file
// lets the FIRST valid row win and rejects the later ones (the DB upsert is
// keyed on email, so this keeps the report deterministic).
export function validateRows(
  rows: RawRepRow[],
  regions: YakRegion[]
): { valid: CandidateRep[]; rejected: ImportRowRejection[] } {
  const resolver = buildRegionResolver(regions)
  const valid: CandidateRep[] = []
  const rejected: ImportRowRejection[] = []
  // email -> line of the first accepted row that claimed it.
  const acceptedEmails = new Map<string, number>()

  for (const row of rows) {
    const email = row.email.toLowerCase()
    const emailForReport = email || null

    if (!row.name) {
      rejected.push({
        line: row.line,
        email: emailForReport,
        reason: 'name is required',
      })
      continue
    }
    if (!row.email) {
      rejected.push({
        line: row.line,
        email: null,
        reason: 'email is required',
      })
      continue
    }
    if (!isPlausibleEmail(email)) {
      rejected.push({
        line: row.line,
        email: emailForReport,
        reason: `“${row.email}” is not a valid email address`,
      })
      continue
    }

    // Phones: split on ';', normalize each, dedupe within the row.
    if (!row.phones) {
      rejected.push({
        line: row.line,
        email: emailForReport,
        reason: 'at least one phone number is required',
      })
      continue
    }
    const tokens = row.phones
      .split(';')
      .map(t => t.trim())
      .filter(t => t.length > 0)
    if (tokens.length === 0) {
      rejected.push({
        line: row.line,
        email: emailForReport,
        reason: 'at least one phone number is required',
      })
      continue
    }
    const phones: string[] = []
    let badPhone: string | null = null
    for (const token of tokens) {
      const norm = normalizePhoneUs(token)
      if (norm === null) {
        badPhone = token
        break
      }
      if (!phones.includes(norm)) phones.push(norm)
    }
    if (badPhone !== null) {
      rejected.push({
        line: row.line,
        email: emailForReport,
        reason: `phone “${badPhone}” is not a valid US phone number`,
      })
      continue
    }

    // Region: trimmed-exact first (names are org-unique case-sensitively),
    // then unique case-insensitive, else not-found / ambiguous.
    if (!row.region) {
      rejected.push({
        line: row.line,
        email: emailForReport,
        reason: 'region is required',
      })
      continue
    }
    let region = resolver.byExact.get(row.region)
    if (!region) {
      const matches = resolver.byLower.get(row.region.toLowerCase())
      if (matches && matches.length === 1) {
        region = matches[0]
      } else if (matches && matches.length > 1) {
        rejected.push({
          line: row.line,
          email: emailForReport,
          reason: `region “${row.region}” matches more than one region — check capitalization`,
        })
        continue
      }
    }
    if (!region) {
      rejected.push({
        line: row.line,
        email: emailForReport,
        reason: `region “${row.region}” not found — regions are never auto-created`,
      })
      continue
    }
    if (resolver.parentIds.has(region.id)) {
      rejected.push({
        line: row.line,
        email: emailForReport,
        reason: `region “${row.region}” has sub-regions — reps must be assigned to a leaf region`,
      })
      continue
    }

    // Everything else passed; the first valid row for an email wins.
    const firstLine = acceptedEmails.get(email)
    if (firstLine !== undefined) {
      rejected.push({
        line: row.line,
        email: emailForReport,
        reason: `email ${email} was already listed on line ${firstLine}`,
      })
      continue
    }
    acceptedEmails.set(email, row.line)
    valid.push({
      line: row.line,
      name: row.name,
      email,
      phones,
      region_id: region.id,
    })
  }

  return { valid, rejected }
}

// Map trimmed/lowercased header cells to column indices, accepting `phone`
// as an alias for `phones`. Returns the index map or the missing columns.
function resolveHeader(
  header: string[]
): { map: Record<string, number> } | { missing: string[] } {
  const idx: Record<string, number> = {}
  header.forEach((h, i) => {
    let key = h.trim().toLowerCase()
    if (key === 'phone') key = 'phones'
    if (
      (REQUIRED_COLUMNS as readonly string[]).includes(key) &&
      !(key in idx)
    ) {
      idx[key] = i
    }
  })
  const missing = REQUIRED_COLUMNS.filter(k => !(k in idx))
  if (missing.length > 0) return { missing }
  return { map: idx }
}

// Top-level entry the screen calls: parse the file text, validate the
// header, then validate each row. A missing header column or empty file is
// a file-level error (nothing imports); otherwise candidates + rejections.
export function parseAndValidate(
  text: string,
  regions: YakRegion[]
): ParsedImport {
  const grid = parseCsv(text)
  if (grid.length === 0) {
    return {
      ok: false,
      error:
        'This file is empty. Expected a header row (name, phones, email, region) and at least one rep.',
    }
  }
  const header = resolveHeader(grid[0])
  if ('missing' in header) {
    return {
      ok: false,
      error: `This file is missing required column(s): ${header.missing.join(
        ', '
      )}. Expected columns: name, phones, email, region.`,
    }
  }
  const { map } = header
  const rawRows: RawRepRow[] = []
  for (let r = 1; r < grid.length; r++) {
    const cells = grid[r]
    rawRows.push({
      // grid[0] is CSV line 1 (header); grid[r] is line r + 1.
      line: r + 1,
      name: (cells[map.name] ?? '').trim(),
      email: (cells[map.email] ?? '').trim(),
      phones: (cells[map.phones] ?? '').trim(),
      region: (cells[map.region] ?? '').trim(),
    })
  }
  const { valid, rejected } = validateRows(rawRows, regions)
  return { ok: true, candidates: valid, rejections: rejected }
}
